/**
 * Utilitários para processamento de geometrias GeoJSON
 */
import { validateAndFixCoordinates } from './validation';
import { needsCoordinateInversion, getSampleCoordinate } from './detection';

/**
 * Parse seguro de JSON com tratamento de erro
 * @param {string} str - String JSON para converter
 * @returns {object|null} - Objeto convertido ou null em caso de erro
 */
export function safeParse(str) {
  if (typeof str !== 'string') {
    // Se já for um objeto, retorna diretamente
    if (str !== null && typeof str === 'object') {
      return str;
    }
    return null;
  }

  try {
    return JSON.parse(str);
  } catch (_error) {
    //console.error('[GeometryService] Erro ao fazer parse de JSON:', error);
    return null;
  }
}

/**
 * Processa uma geometria GeoJSON (string ou objeto)
 * @param {string|object} geojson - GeoJSON em string ou objeto
 * @returns {object|null} - GeoJSON processado ou null em caso de erro
 */
export function processGeoJSON(geojson) {
  // Parse seguro para garantir que temos um objeto
  const parsedGeoJSON = safeParse(geojson);
  if (!parsedGeoJSON) return null;

  // Valida e corrige as coordenadas se necessário
  try {
    // Detecta se precisa inverter coordenadas
    const needsInversion = needsCoordinateInversion(parsedGeoJSON);

    if (needsInversion) {
      //console.log('[GeometryService] Invertendo coordenadas de geometria');
      // Inverte as coordenadas conforme necessário
      const invertedCoordinates = invertCoordinatesDeep(parsedGeoJSON.coordinates);
      return {
        ...parsedGeoJSON,
        coordinates: invertedCoordinates,
      };
    }

    // Se não precisa inverter, apenas valida e corrige formatação
    const validatedCoordinates = validateAndFixCoordinates(parsedGeoJSON.coordinates);
    if (!validatedCoordinates) return null;

    return {
      ...parsedGeoJSON,
      coordinates: validatedCoordinates,
    };
  } catch (_error) {
    //console.error('[GeometryService] Erro ao processar GeoJSON:', error);
    return null;
  }
}

/**
 * Função auxiliar para inverter coordenadas em estruturas profundas
 * @param {Array} coordinates - Array de coordenadas (possivelmente aninhado)
 * @returns {Array} - Coordenadas invertidas
 */
function invertCoordinatesDeep(coordinates) {
  if (!coordinates || !Array.isArray(coordinates)) return coordinates;

  // Caso base: par de coordenadas [x, y]
  if (
    coordinates.length === 2 &&
    typeof coordinates[0] === 'number' &&
    typeof coordinates[1] === 'number'
  ) {
    return [coordinates[1], coordinates[0]]; // Inverte x e y
  }

  // Caso recursivo: array de coordenadas
  return coordinates.map((coord) => invertCoordinatesDeep(coord));
}

/**
 * Processa dados de propriedade conforme estrutura definida do backend:
 * {
 *   properties: {
 *     car: [
 *       {
 *         cod_imovel: "...",
 *         themes: [
 *           { nome_tema: "...", geojson: {...} },
 *           ...
 *         ]
 *       }
 *     ],
 *     sigef: [
 *       {
 *         codigo_imo: "...",
 *         parcels: [
 *           { parcela_co: "...", geojson: {...} },
 *           ...
 *         ]
 *       }
 *     ]
 *   }
 * }
 * @param {object} propertyData - Dados da propriedade
 * @returns {object} - { carGeometries, sigefGeometries, themeGeometries }
 */
export function processPropertyGeometries(propertyData) {
  // Arrays para armazenamento das geometrias processadas
  const carGeometries = [];
  const sigefGeometries = [];
  const _geometries = [];
  // Objeto para armazenamento das geometrias por tema (formato requerido por GeometryLayers)
  const themeGeometries = {};

  // Extrair filename/file_id de todas as possíveis localizações
  let file_id = null;

  // Verificar em propriedades e metadados
  if (propertyData?._metadata?.file_id) {
    file_id = propertyData._metadata.file_id;
    //console.log(`[GeometryService] Usando file_id dos metadados: ${file_id}`);
  }

  try {
    //console.log('[GeometryService] Iniciando processamento de geometrias');

    if (!propertyData) {
      console.error('[GeometryService] Dados de propriedade indefinidos ou nulos');
      return { carGeometries, sigefGeometries, themeGeometries };
    }

    //console.log('[GeometryService] Estrutura de dados recebida:', Object.keys(propertyData));

    // Determina a localização dos dados car e sigef, verificando todas as possíveis estruturas
    let carData = null;
    let sigefData = null;

    // Estrutura 1 (canônica FE): properties.car/sigef (payload já adaptado)
    if (propertyData.properties && typeof propertyData.properties === 'object') {
      //console.log('[GeometryService] Estrutura encontrada: propertyData.properties');
      carData = propertyData.properties.car;
      sigefData = propertyData.properties.sigef;
    }
    // Estrutura 2 (contrato BE novo): gn_properties.car/sigef na raiz
    else if (propertyData.gn_properties && typeof propertyData.gn_properties === 'object') {
      carData = propertyData.gn_properties.car;
      sigefData = propertyData.gn_properties.sigef;
    }
    // Estrutura 3 (envelope): data.gn_properties.car/sigef
    else if (
      propertyData.data &&
      propertyData.data.gn_properties &&
      typeof propertyData.data.gn_properties === 'object'
    ) {
      carData = propertyData.data.gn_properties.car;
      sigefData = propertyData.data.gn_properties.sigef;
    }
    // Estrutura 4 (legado/envelope antigo): data.properties.car/sigef
    else if (propertyData.data && propertyData.data.properties) {
      carData = propertyData.data.properties.car;
      sigefData = propertyData.data.properties.sigef;
    }
    // Estrutura 5: car/sigef diretamente no objeto raiz (após extração)
    else if (propertyData.car || propertyData.sigef) {
      //console.log('[GeometryService] Estrutura encontrada: car/sigef no nível raiz');
      carData = propertyData.car;
      sigefData = propertyData.sigef;
    }

    //console.log('[GeometryService] Resultado da detecção de estrutura:', {
    //  hasDataProperty: !!propertyData.data,
    //  hasPropertiesAtRoot: !!propertyData.properties,
    //  hasCarAtRoot: !!propertyData.car,
    //  hasSigefAtRoot: !!propertyData.sigef,
    //  carDataFound: !!carData,
    //  sigefDataFound: !!sigefData
    //});

    // Processa os dados encontrados
    if (carData || sigefData) {
      //console.log('[GeometryService] Dados encontrados para processar:', {
      //  hasCar: !!carData,
      //  hasSigef: !!sigefData
      //});

      processCar(carData);
      processSigef(sigefData);
    } else {
      //console.warn('[GeometryService] Nenhum dado car/sigef encontrado na estrutura');
    }

    // Função local para processar dados CAR
    function processCar(carData) {
      if (!carData || !Array.isArray(carData)) {
        //console.log('[GeometryService] Nenhum dado car encontrado');
        return;
      }

      //console.log(`[GeometryService] Processando ${carData.length} itens car`);

      // Para cada item do array car
      carData.forEach((car) => {
        if (!car) return;

        //console.log('[GeometryService] Processando car com chaves:', Object.keys(car));

        // Verifica se temos array themes
        const themes = car.themes;
        if (!themes || !Array.isArray(themes)) {
          //console.log('[GeometryService] Item car sem array themes');
          return;
        }

        //console.log(`[GeometryService] Processando ${themes.length} temas para cod_imovel: ${car.cod_imovel}`);

        // Para cada tema dentro do array themes
        themes.forEach((theme) => {
          if (!theme) return;

          // Extrai a geometria do tema
          const geojsonData = theme.geojson;
          if (!geojsonData) {
            //console.log('[GeometryService] Tema sem geojson:', theme.nome_tema || 'sem nome');
            return;
          }

          // Processa a geometria
          const processedGeom = processGeoJSON(geojsonData);
          if (!processedGeom) {
            //console.log('[GeometryService] Falha ao processar geojson do tema:', theme.nome_tema || 'sem nome');
            return;
          }

          // Nome do tema para usar como chave no objeto themeGeometries
          const nomeTema = theme.nome_tema ? theme.nome_tema.toUpperCase() : 'SEM_NOME';

          // Inicializa o array para o tema se não existir
          if (!themeGeometries[nomeTema]) {
            themeGeometries[nomeTema] = [];
          }

          // Adiciona como feature GeoJSON completa, não apenas geometry
          themeGeometries[nomeTema].push({
            type: 'Feature',
            geometry: processedGeom,
            properties: {
              tema: nomeTema,
              codigo_imovel: car.cod_imovel || '',
              area: theme.area || 0,
              source: 'car',
              filename: file_id ? `${file_id}.json` : null,
            },
          });

          // Se este tema representa a área do imóvel, adiciona às geometrias car
          // Identifica pelo nome do tema ou pelo primeiro tema se não houver nome
          const nomeTemaTitulo = theme.nome_tema || '';
          if (
            (nomeTemaTitulo.toUpperCase().includes('REA') &&
              nomeTemaTitulo.toUpperCase().includes('IMO')) ||
            nomeTemaTitulo.toUpperCase() === 'ÁREA DO IMÓVEL' ||
            themes.length === 1
          ) {
            //console.log(`[GeometryService] Área do imóvel car identificada: ${nomeTemaTitulo}`);
            carGeometries.push({
              geojson: processedGeom,
              metadata: {
                codigo_imovel: car.cod_imovel || '',
                area: theme.area || 0,
              },
            });
          }
        });
      });
    }

    // Função local para processar dados SIGEF
    function processSigef(sigefData) {
      if (!sigefData || !Array.isArray(sigefData)) {
        //console.log('[GeometryService] Nenhum dado sigef encontrado');
        return;
      }

      //console.log(`[GeometryService] Processando ${sigefData.length} itens sigef`);

      // Para cada item do array sigef
      sigefData.forEach((sigef) => {
        if (!sigef) return;

        //console.log('[GeometryService] Processando sigef com chaves:', Object.keys(sigef));

        // Verifica se temos array parcels
        const parcels = sigef.parcels;
        if (!parcels || !Array.isArray(parcels)) {
          //console.log('[GeometryService] Item sigef sem array parcels');
          return;
        }

        //console.log(`[GeometryService] Processando ${parcels.length} parcelas para codigo_imo: ${sigef.codigo_imo}`);

        // Para cada parcela dentro do array parcels
        parcels.forEach((parcel) => {
          if (!parcel) return;

          // Extrai a geometria da parcela
          const geojsonData = parcel.geojson;
          if (!geojsonData) {
            //console.log('[GeometryService] Parcela sem geojson:', parcel.parcela_co || 'sem código');
            return;
          }

          // Processa a geometria
          const processedGeom = processGeoJSON(geojsonData);
          if (!processedGeom) {
            //console.log('[GeometryService] Falha ao processar geojson da parcela:', parcel.parcela_co || 'sem código');
            return;
          }

          // Adiciona às geometrias sigef
          //console.log(`[GeometryService] Geometria encontrada na parcela: ${parcel.parcela_co || 'sem código'}`);
          sigefGeometries.push({
            geojson: processedGeom,
            metadata: {
              codigo_imo: sigef.codigo_imo || '',
              parcela_co: parcel.parcela_co || '',
            },
          });
        });
      });
    }

    // Adicionar todas as geometrias SIGEF em uma camada específica
    if (sigefGeometries.length > 0) {
      if (!themeGeometries['SIGEF']) {
        themeGeometries['SIGEF'] = [];
      }

      // Adiciona todas as geometrias SIGEF à camada SIGEF como features GeoJSON completas
      sigefGeometries.forEach((sigef) => {
        if (sigef && sigef.geojson) {
          themeGeometries['SIGEF'].push({
            type: 'Feature',
            geometry: sigef.geojson,
            properties: {
              tema: 'SIGEF',
              parcela_co: sigef.metadata?.parcela_co || '',
              codigo_imo: sigef.metadata?.codigo_imo || '',
              source: 'sigef',
              filename: file_id ? `${file_id}.json` : null,
            },
          });
        }
      });
    }

    //console.log('[GeometryService] Processamento concluído:', {
    //  carGeometries: carGeometries.length,
    //  sigefGeometries: sigefGeometries.length,
    //  temas: Object.keys(themeGeometries),
    //  contagemPorTema: Object.entries(themeGeometries).map(([tema, geoms]) => `${tema}: ${geoms.length}`)
    //});

    // Preparar array de geometrias combinadas
    const allGeometries = [];

    // Adicionar geometrias CAR
    carGeometries.forEach((item) => {
      if (item && item.geojson) {
        allGeometries.push(item.geojson);
      }
    });

    // Adicionar geometrias SIGEF
    sigefGeometries.forEach((item) => {
      if (item && item.geojson) {
        allGeometries.push(item.geojson);
      }
    });

    return {
      carGeometries,
      sigefGeometries,
      themeGeometries,
      // Geometrias combinadas para facilitar o rendering
      geometries: allGeometries,
    };
  } catch (error) {
    console.error('[GeometryService] Erro ao processar geometrias:', error);
    return { carGeometries: [], sigefGeometries: [], themeGeometries: [], geometries: [] };
  }
}

/**
 * Calcula o centroide de um conjunto de geometrias
 * @param {Array} geometries - Array de objetos de geometria
 * @returns {Object|null} - Objeto {lat, lng} com o centroide ou null se não puder calcular
 */
export function calculateCentroid(geometries) {
  try {
    // Verifica se há geometrias para processar
    if (!geometries || !Array.isArray(geometries) || geometries.length === 0) {
      console.error('[GeometryService] Nenhuma geometria disponível para cálculo de centroide');
      return null;
    }

    //console.log('[GeometryService] Amostra de estrutura de geometria:',
    //          JSON.stringify(geometries[0]).substring(0, 150));

    // Primeiro tenta encontrar uma geometria de área do imóvel (preferencial)
    const areaImovelGeometry = geometries.find((g) => {
      // Verifica formato antigo (metadata)
      if (g && g.metadata) {
        return (
          g.metadata.alias === 'Area_Imovel' ||
          g.metadata.nome_tema === 'Área do Imóvel' ||
          g.metadata.nome_tema === 'AREA DO IMOVEL'
        );
      }
      // Verifica formato novo (properties)
      if (g && g.properties) {
        return g.properties.tema === 'ÁREA DO IMÓVEL' || g.properties.tema === 'AREA DO IMOVEL';
      }
      return false;
    });

    // Tenta obter a geometria do objeto encontrado (formato antigo ou novo)
    let targetGeojson = null;

    if (areaImovelGeometry) {
      if (areaImovelGeometry.geojson) {
        // Formato antigo
        targetGeojson = areaImovelGeometry.geojson;
      } else if (areaImovelGeometry.geometry) {
        // Formato novo (GeoJSON Feature)
        targetGeojson = areaImovelGeometry.geometry;
      }
    }

    if (targetGeojson) {
      // Calcula o centroide com base no tipo de geometria
      switch (targetGeojson.type) {
        case 'Point':
          return { lat: targetGeojson.coordinates[1], lng: targetGeojson.coordinates[0] };

        case 'LineString':
          // Média simples dos pontos para LineString
          // eslint-disable-next-line no-case-declarations
          const middleIndex = Math.floor(targetGeojson.coordinates.length / 2);
          // eslint-disable-next-line no-case-declarations
          const middlePoint = targetGeojson.coordinates[middleIndex];
          return { lat: middlePoint[1], lng: middlePoint[0] };

        case 'Polygon':
          // Para polígonos, usamos um algoritmo para encontrar o centro aproximado
          // eslint-disable-next-line no-case-declarations
          const bounds = calculateBounds(targetGeojson.coordinates[0]); // Pegamos o anel externo
          return {
            lat: (bounds.maxLat + bounds.minLat) / 2,
            lng: (bounds.maxLng + bounds.minLng) / 2,
          };

        case 'MultiPolygon':
          // Para multipolígonos, calculamos o centroide do maior polígono
          // eslint-disable-next-line no-case-declarations
          let largestArea = 0;
          // eslint-disable-next-line no-case-declarations
          let largestPolygonCentroid = null;

          // Itera sobre cada polígono para encontrar o maior
          targetGeojson.coordinates.forEach((polygon) => {
            const polyBounds = calculateBounds(polygon[0]); // Anel externo
            const area =
              (polyBounds.maxLng - polyBounds.minLng) * (polyBounds.maxLat - polyBounds.minLat);

            if (area > largestArea) {
              largestArea = area;
              largestPolygonCentroid = {
                lat: (polyBounds.maxLat + polyBounds.minLat) / 2,
                lng: (polyBounds.maxLng + polyBounds.minLng) / 2,
              };
            }
          });

          return largestPolygonCentroid;

        default:
          //console.error('[GeometryService] Tipo de geometria não suportado para cálculo de centroide:', geojson.type);
          return null;
      }
    } else {
      //console.log('[GeometryService] Se não encontrou uma geometria de área do imóvel, usa a primeira geometria disponível
      // Procura por qualquer geometria válida (formato antigo ou novo)
      for (const geom of geometries) {
        let geometry = null;

        // Tenta obter a geometria no formato antigo ou novo
        if (geom && geom.geojson) {
          // Formato antigo
          geometry = geom.geojson;
        } else if (geom && geom.geometry) {
          // Formato novo (GeoJSON Feature)
          geometry = geom.geometry;
        } else if (
          geom &&
          geom.type &&
          (geom.type === 'Feature' ||
            geom.type === 'Point' ||
            geom.type === 'Polygon' ||
            geom.type === 'MultiPolygon')
        ) {
          // Pode ser diretamente um objeto geometry ou feature
          geometry = geom.type === 'Feature' ? geom.geometry : geom;
        }

        if (geometry) {
          // Tenta obter coordenadas da geometria encontrada
          const coords = getSampleCoordinate(geometry);

          if (coords && coords.length >= 2) {
            return { lat: coords[1], lng: coords[0] };
          }
        }
      }

      //console.error('[GeometryService] Não foi possível extrair coordenadas para cálculo de centroide');
      return null;
    }
  } catch (error) {
    console.error('[GeometryService] Erro ao calcular centroide:', error);
    // Fallback para uma coordenada fixa no Brasil central
    return { lat: -15.798889, lng: -47.866667 }; // Brasília
  }
}

/**
 * Função auxiliar para calcular limites de um polígono
 * @param {Array} coordinates - Array de coordenadas do polígono
 * @returns {Object} - Objeto com limites {minLng, maxLng, minLat, maxLat}
 */
function calculateBounds(coordinates) {
  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coordinates.forEach((coord) => {
    const [lng, lat] = coord;

    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return { minLng, maxLng, minLat, maxLat };
}
